import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// Types
// ============================================
interface PipelineStep {
  id: string;
  name: string;
  icon: string;
  status: 'pending' | 'active' | 'completed';
  duration?: string;
  output?: string;
}

interface BlogPost {
  id: string;
  title: string;
  keyword: string;
  status: 'queued' | 'processing' | 'published';
  publishedAt?: string;
  wordCount?: number;
  readTime?: string;
}

// ============================================
// Mock Data
// ============================================
const mockPipelineSteps: PipelineStep[] = [
  { id: 'trigger', name: 'Schedule Trigger', icon: '⏰', status: 'completed', duration: '0s' },
  { id: 'sheets', name: 'Fetch Keywords', icon: '📊', status: 'completed', duration: '1.2s', output: 'Primary: "Bali villa investment ROI"' },
  { id: 'plan', name: 'Create Plan', icon: '📝', status: 'completed', duration: '8.4s', output: '6 sections, 12 keywords mapped' },
  { id: 'research', name: 'AI Research', icon: '🔍', status: 'completed', duration: '12.1s', output: '8 sources found, 5 citations added' },
  { id: 'write', name: 'Write Content', icon: '✍️', status: 'active', duration: '~45s', output: 'Generating 2,400 words...' },
  { id: 'polish', name: 'Polish & SEO', icon: '✨', status: 'pending' },
  { id: 'links', name: 'Internal Links', icon: '🔗', status: 'pending' },
  { id: 'images', name: 'Generate Images', icon: '🖼️', status: 'pending' },
  { id: 'publish', name: 'Publish to WP', icon: '🚀', status: 'pending' },
];

const mockBlogPosts: BlogPost[] = [
  { id: '1', title: 'The Complete Guide to Bali Villa Investment ROI in 2025', keyword: 'bali villa investment roi', status: 'published', publishedAt: '2 hours ago', wordCount: 2847, readTime: '12 min' },
  { id: '2', title: 'Foreign Ownership Laws in Bali: What Investors Need to Know', keyword: 'foreign ownership bali', status: 'published', publishedAt: '1 day ago', wordCount: 3102, readTime: '14 min' },
  { id: '3', title: 'Best Areas to Buy Property in Bali for Maximum Returns', keyword: 'best areas bali property', status: 'published', publishedAt: '2 days ago', wordCount: 2654, readTime: '11 min' },
  { id: '4', title: 'Leasehold vs Freehold in Bali: A Complete Comparison', keyword: 'leasehold freehold bali', status: 'processing', wordCount: 2200 },
  { id: '5', title: 'How to Calculate Net ROI on Bali Rental Villas', keyword: 'calculate roi bali villa', status: 'queued' },
];

const mockKeywords = [
  { keyword: 'bali villa investment', volume: '2,400/mo', difficulty: 'Medium', status: 'Published' },
  { keyword: 'bali property roi', volume: '1,900/mo', difficulty: 'Medium', status: 'Published' },
  { keyword: 'foreign ownership bali', volume: '1,600/mo', difficulty: 'Low', status: 'Published' },
  { keyword: 'leasehold vs freehold bali', volume: '880/mo', difficulty: 'Low', status: 'In Progress' },
  { keyword: 'bali rental yield', volume: '720/mo', difficulty: 'Medium', status: 'Queued' },
  { keyword: 'best areas bali invest', volume: '590/mo', difficulty: 'High', status: 'Queued' },
];

const mockBlogContent = `# The Complete Guide to Bali Villa Investment ROI in 2025

*Estimated Reading Time: 12 minutes*

Investing in Bali real estate has become one of the most talked-about opportunities in Southeast Asia. But behind the Instagram-worthy infinity pools and tropical sunsets lies a serious question every investor must answer: **What's the actual ROI?**

## Key Takeaways

- Average gross rental yields in Bali range from 8-15% annually
- Net ROI after expenses typically falls between 6-10%
- Location, lease length, and management quality are the three biggest factors
- Off-plan investments can offer 20-35% capital appreciation at completion

## Why Bali Villa Investment Stands Out

Unlike traditional real estate markets, Bali offers a unique combination of factors...

### The Tourism Factor

With over 6.3 million international visitors in 2024 [source: Bali Tourism Board], the island's rental demand continues to surge. This translates directly into...

### Understanding Gross vs Net ROI

Many developers advertise impressive 12-15% returns. But here's what they don't tell you...

**Gross ROI Calculation:**
- Annual Rental Income: $42,000
- Purchase Price: $350,000
- Gross ROI: 12%

**Net ROI (After Expenses):**
- Management fees (20-25%)
- Maintenance & repairs
- Platform commissions
- Taxes & permits
- **Actual Net ROI: 7.8%**

## The 5 Factors That Determine Your ROI

### 1. Location Matters More Than You Think

Seminyak, Canggu, and Uluwatu command premium rates but also higher purchase prices...`;

// ============================================
// Icon Components
// ============================================
const WordPressIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 1.5c4.687 0 8.5 3.813 8.5 8.5 0 4.687-3.813 8.5-8.5 8.5-4.687 0-8.5-3.813-8.5-8.5 0-4.687 3.813-8.5 8.5-8.5zM4.5 12c0 1.846.673 3.535 1.783 4.84L10.449 6.49A7.474 7.474 0 004.5 12zm7.5 7.5c-.725 0-1.424-.104-2.086-.297l2.214-6.437 2.27 6.218c.015.036.033.07.051.103A7.448 7.448 0 0112 19.5zm1.065-11.026c.444-.023.845-.07.845-.07.398-.047.351-.632-.047-.609 0 0-1.196.094-1.968.094-.725 0-1.945-.094-1.945-.094-.398-.023-.445.586-.047.609 0 0 .378.047.775.07l1.152 3.157-1.617 4.853-2.692-8.01c.445-.023.845-.07.845-.07.398-.047.351-.632-.047-.609 0 0-1.196.094-1.968.094-.139 0-.302-.004-.473-.009A7.468 7.468 0 0112 4.5c1.908 0 3.65.713 4.975 1.888-.032-.002-.063-.007-.095-.007-.725 0-1.24.632-1.24 1.311 0 .609.351 1.125.725 1.734.281.492.609 1.125.609 2.039 0 .632-.242 1.365-.562 2.386l-.736 2.46-2.664-7.926-.047.024z"/>
  </svg>
);

const GoogleSheetsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 11V9h-6V3H5v18h14v-6h-6v-4h6zM7 7h4v2H7V7zm0 4h4v2H7v-2zm0 4h4v2H7v-2zm10 2h-4v-2h4v2z"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// ============================================
// Pipeline Step Component
// ============================================
const PipelineStepCard: React.FC<{ step: PipelineStep; index: number }> = ({ step, index }) => {
  const statusColors = {
    pending: 'bg-zinc-800 border-zinc-700 text-zinc-500',
    active: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
    completed: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`relative flex items-center gap-3 p-2.5 rounded-lg border ${statusColors[step.status]}`}
    >
      {/* Status indicator line */}
      {index < mockPipelineSteps.length - 1 && (
        <div className={`absolute left-5 top-full w-0.5 h-2 ${step.status === 'completed' ? 'bg-emerald-500/50' : 'bg-zinc-700'}`} />
      )}

      {/* Icon */}
      <div className="w-6 h-6 flex items-center justify-center text-sm">
        {step.status === 'active' ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            ⚙️
          </motion.div>
        ) : (
          step.icon
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium truncate">{step.name}</div>
        {step.output && (
          <div className="text-[9px] opacity-70 truncate">{step.output}</div>
        )}
      </div>

      {/* Duration */}
      {step.duration && (
        <div className="text-[9px] opacity-50">{step.duration}</div>
      )}

      {/* Status icon */}
      {step.status === 'completed' && (
        <div className="text-emerald-400">
          <CheckCircleIcon />
        </div>
      )}
      {step.status === 'active' && (
        <motion.div
          className="w-2 h-2 bg-blue-400 rounded-full"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
    </motion.div>
  );
};

// ============================================
// Blog Preview Component
// ============================================
const BlogPreview: React.FC = () => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < mockBlogContent.length) {
        setDisplayedContent(mockBlogContent.slice(0, index + 10));
        index += 10;
      } else {
        setIsTyping(false);
        clearInterval(interval);
      }
    }, 20);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-white rounded-lg overflow-hidden h-full flex flex-col">
      {/* Browser chrome */}
      <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-white rounded px-3 py-1 text-[10px] text-gray-500 truncate">
          investlandbali.com/blog/bali-villa-investment-roi-guide
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 text-gray-800">
        <div className="prose prose-sm max-w-none">
          {displayedContent.split('\n').map((line, i) => {
            if (line.startsWith('# ')) {
              return <h1 key={i} className="text-xl font-bold text-gray-900 mb-4">{line.replace('# ', '')}</h1>;
            }
            if (line.startsWith('## ')) {
              return <h2 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-3">{line.replace('## ', '')}</h2>;
            }
            if (line.startsWith('### ')) {
              return <h3 key={i} className="text-base font-semibold text-gray-700 mt-4 mb-2">{line.replace('### ', '')}</h3>;
            }
            if (line.startsWith('*') && line.endsWith('*')) {
              return <p key={i} className="text-gray-500 italic text-xs mb-4">{line.replace(/\*/g, '')}</p>;
            }
            if (line.startsWith('- ')) {
              return <li key={i} className="text-[11px] text-gray-600 ml-4">{line.replace('- ', '')}</li>;
            }
            if (line.startsWith('**') && line.includes(':')) {
              return <p key={i} className="text-[11px] text-gray-600 font-medium">{line.replace(/\*\*/g, '')}</p>;
            }
            if (line.trim()) {
              return <p key={i} className="text-[11px] text-gray-600 mb-2">{line}</p>;
            }
            return null;
          })}
          {isTyping && (
            <motion.span
              className="inline-block w-2 h-4 bg-blue-500"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Keywords Queue Component
// ============================================
const KeywordsQueue: React.FC = () => (
  <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-3">
    <div className="flex items-center gap-2 mb-3">
      <div className="text-emerald-400">
        <GoogleSheetsIcon />
      </div>
      <span className="text-xs font-semibold text-white">Keyword Queue</span>
      <span className="ml-auto px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[9px] rounded">
        6 pending
      </span>
    </div>

    <div className="space-y-1.5">
      {mockKeywords.map((kw, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 p-2 rounded-lg text-[10px] ${
            kw.status === 'Published' ? 'bg-emerald-500/10 border border-emerald-500/20' :
            kw.status === 'In Progress' ? 'bg-blue-500/10 border border-blue-500/20' :
            'bg-zinc-800/50 border border-zinc-700'
          }`}
        >
          <span className={`flex-1 truncate ${
            kw.status === 'Published' ? 'text-emerald-400' :
            kw.status === 'In Progress' ? 'text-blue-400' :
            'text-zinc-400'
          }`}>
            {kw.keyword}
          </span>
          <span className="text-zinc-500 text-[9px]">{kw.volume}</span>
          <span className={`px-1.5 py-0.5 rounded text-[8px] ${
            kw.status === 'Published' ? 'bg-emerald-500/20 text-emerald-400' :
            kw.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400' :
            'bg-zinc-700 text-zinc-400'
          }`}>
            {kw.status}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// Recent Posts Component
// ============================================
const RecentPosts: React.FC = () => (
  <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-3">
    <div className="flex items-center gap-2 mb-3">
      <div className="text-blue-400">
        <WordPressIcon />
      </div>
      <span className="text-xs font-semibold text-white">Published Posts</span>
    </div>

    <div className="space-y-2">
      {mockBlogPosts.slice(0, 4).map((post) => (
        <div
          key={post.id}
          className="p-2 rounded-lg bg-zinc-800/50 border border-zinc-700"
        >
          <div className="flex items-start gap-2">
            <div className={`mt-0.5 ${
              post.status === 'published' ? 'text-emerald-400' :
              post.status === 'processing' ? 'text-blue-400' :
              'text-zinc-500'
            }`}>
              {post.status === 'published' ? <CheckCircleIcon /> :
               post.status === 'processing' ? <ClockIcon /> :
               <DocumentIcon />}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-[10px] font-medium text-white truncate">{post.title}</h4>
              <div className="flex items-center gap-2 mt-1 text-[9px] text-zinc-500">
                {post.publishedAt && <span>{post.publishedAt}</span>}
                {post.wordCount && <span>{post.wordCount.toLocaleString()} words</span>}
                {post.readTime && <span>{post.readTime} read</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// Stats Component
// ============================================
const StatsPanel: React.FC = () => (
  <div className="grid grid-cols-4 gap-2">
    <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-2.5 text-center">
      <div className="text-lg font-bold text-emerald-400">47</div>
      <div className="text-[9px] text-zinc-500">Posts Published</div>
    </div>
    <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-2.5 text-center">
      <div className="text-lg font-bold text-blue-400">124K</div>
      <div className="text-[9px] text-zinc-500">Total Words</div>
    </div>
    <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-2.5 text-center">
      <div className="text-lg font-bold text-purple-400">89%</div>
      <div className="text-[9px] text-zinc-500">SEO Score Avg</div>
    </div>
    <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-2.5 text-center">
      <div className="text-lg font-bold text-orange-400">12</div>
      <div className="text-[9px] text-zinc-500">Ranking Page 1</div>
    </div>
  </div>
);

// ============================================
// Main Demo Component
// ============================================
export const SEOBlogAutomationDemo: React.FC = () => {
  const [pipelineSteps, setPipelineSteps] = useState(mockPipelineSteps);
  const [activeView, setActiveView] = useState<'pipeline' | 'preview'>('pipeline');

  // Simulate pipeline progression
  useEffect(() => {
    const interval = setInterval(() => {
      setPipelineSteps(steps => {
        const activeIndex = steps.findIndex(s => s.status === 'active');
        if (activeIndex === -1 || activeIndex >= steps.length - 1) {
          // Reset to beginning
          return mockPipelineSteps;
        }

        return steps.map((step, i) => {
          if (i === activeIndex) {
            return { ...step, status: 'completed' as const };
          }
          if (i === activeIndex + 1) {
            return { ...step, status: 'active' as const };
          }
          return step;
        });
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative">
      {/* Demo Badge */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <span className="px-2.5 py-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 text-xs font-medium rounded-full backdrop-blur-sm">
          Live Demo
        </span>
      </div>

      {/* Main Container */}
      <div className="rounded-xl overflow-hidden border border-zinc-800 bg-[#0a0a0a]">
        {/* Header */}
        <div className="bg-[#0d1117] border-b border-[#21262d] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
              AI
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold">SEO Blog Automation</h3>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>Running on schedule</span>
              </div>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 bg-zinc-800 rounded-lg p-0.5">
            <button
              onClick={() => setActiveView('pipeline')}
              className={`px-3 py-1 text-[10px] font-medium rounded-md transition-colors ${
                activeView === 'pipeline' ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setActiveView('preview')}
              className={`px-3 py-1 text-[10px] font-medium rounded-md transition-colors ${
                activeView === 'preview' ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Preview
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-[450px] flex">
          <AnimatePresence mode="wait">
            {activeView === 'pipeline' ? (
              <motion.div
                key="pipeline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 flex"
              >
                {/* Left: Pipeline */}
                <div className="w-64 border-r border-[#21262d] p-3 overflow-y-auto">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-3">Current Pipeline</div>
                  <div className="space-y-2">
                    {pipelineSteps.map((step, i) => (
                      <PipelineStepCard key={step.id} step={step} index={i} />
                    ))}
                  </div>
                </div>

                {/* Right: Stats & Queue */}
                <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                  <StatsPanel />
                  <div className="grid grid-cols-2 gap-3">
                    <KeywordsQueue />
                    <RecentPosts />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="preview"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 p-3"
              >
                <BlogPreview />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Info Bar */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs text-zinc-400">Automated Pipeline</span>
          </div>
          <span className="text-xs text-zinc-600">|</span>
          <span className="text-xs text-zinc-500">Publishes 2-3 SEO-optimized posts daily</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Powered by</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">GPT-4</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">n8n</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">WordPress</span>
        </div>
      </div>
    </div>
  );
};

export default SEOBlogAutomationDemo;
